import InputMultiEmail from "components/Adapters/input-multi-email";
import Button from "components/button";
import GenericModal from "components/generic-modal";
import CloseIcon from "components/icons/icn-close.icon";
import { useGlobalContext } from "hooks/useGlobalContext";
import moment from "moment";
import { useEffect, useState } from "react";
import intl from "react-intl-universal";
import { acceptSuggestionsWithStockReturnNotification } from "services/suggestion/suggestion.service";
import { formatNumber } from "utils/format";
import { formatErrorMessage } from "utils/handle-error";
import { isStockReturnSuggestion } from "../../utils/suggestion-type";
import * as S from "./styled";

type SuggestionId = number | string;
type NullableSuggestionId = SuggestionId | null;

export interface StockReturnSuggestion {
  Id: SuggestionId;
  SuggestionType?: unknown;
  SuggestionTypeDescription?: string;
  LabeledFamilYCenterId?: NullableSuggestionId;
  SupplierName?: string;
  Origin?: string;
  BusinessUnit?: string;
  BusinessUnitPlant?: string;
  BusinessUnitName?: string;
  MaterialNumber?: number | string;
  MaterialName?: string;
  Quantity?: number | string;
  QuantityUnitOfMeasure?: string;
  Lot?: number | string | null;
  LotRoudingUnit?: string;
  SuggestionCollectDate?: string | null;
  SuggestionDeliveryDate?: string | null;
  SuggestionDeliveryTime?: string | null;
}

interface StockReturnNotificationModalProps {
  open: boolean;
  suggestions: StockReturnSuggestion[];
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
  onSubmit?: (stockReturn: StockReturnAcceptancePayload) => Promise<unknown>;
}

export interface StockReturnAcceptancePayload {
  items: { suggestionId: SuggestionId; poReference: string }[];
  notification: { recipients: string[]; title: string };
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getAcceptanceFailureMessage = (response: unknown): string | null => {
  type AcceptanceResult = {
    status?: string;
    message?: string;
    errorCode?: string;
    Status?: string;
    Message?: string;
    ErrorCode?: string;
  };
  const result = response as {
    data?: { results?: AcceptanceResult[]; Results?: AcceptanceResult[] };
    results?: AcceptanceResult[];
    Results?: AcceptanceResult[];
    failures?: { id?: string | number; message?: string[] }[];
  } | null;
  if (result?.failures?.length) {
    return result.failures
      .map((failure) => `${failure.id ?? ""}: ${(failure.message ?? []).join(", ")}`.trim())
      .join("\n");
  }
  const results = result?.data?.results ?? result?.data?.Results ?? result?.results ?? result?.Results;
  const failures = results?.filter(
    (item) => {
      const status = item.status ?? item.Status;
      return status && !["accepted", "alreadyprocessed"].includes(status.toLowerCase());
    }
  );

  if (!failures?.length) return null;

  const failureMessages = failures
    .map((item) =>
      [item.message, item.errorCode, item.Message, item.ErrorCode].find((message) => Boolean(message))
    )
    .filter(Boolean);

  return failureMessages.length > 0
    ? failureMessages.join("\n")
    : intl.get("feedbacks.error");
};

const getText = (value?: string | number | null): string =>
  value === null || value === undefined || value === "" ? "-" : String(value);

const formatDate = (value: string | null | undefined, format: string): string =>
  value ? moment(value).format(format) : "-";

const getMaterialText = (suggestion: StockReturnSuggestion): string => {
  const materialNumberValue = getText(suggestion.MaterialNumber);
  const normalizedMaterialNumber =
    materialNumberValue === "-" ? materialNumberValue : materialNumberValue.replace(/^0+/, "");
  const materialNumber = normalizedMaterialNumber === "" ? "0" : normalizedMaterialNumber;
  const materialName = suggestion.MaterialName?.includes("-")
    ? suggestion.MaterialName.split("-").slice(1).join("-").trim()
    : suggestion.MaterialName;

  if (materialNumber === "-" && !materialName) return "-";
  if (!materialName) return materialNumber;
  if (materialNumber === "-") return materialName;

  return `${materialNumber} - ${materialName}`;
};

const getDestinationText = (suggestion: StockReturnSuggestion): string => {
  if (suggestion.BusinessUnit) return suggestion.BusinessUnit;

  const destinationParts = [suggestion.BusinessUnitPlant, suggestion.BusinessUnitName].filter(
    Boolean
  );
  return destinationParts.length > 0 ? destinationParts.join(" - ") : "-";
};

const getSourceText = (suggestion: StockReturnSuggestion): string =>
  [suggestion.SupplierName, suggestion.Origin].find((value) => Boolean(value)) ?? "-";

const getQuantityText = (suggestion: StockReturnSuggestion): string => {
  if (suggestion.Quantity === null || suggestion.Quantity === undefined) return "-";

  const quantity = formatNumber(Number(suggestion.Quantity));
  return [quantity, suggestion.QuantityUnitOfMeasure].filter(Boolean).join(" ");
};

const getLotText = (suggestion: StockReturnSuggestion): string => {
  if (suggestion.Lot === null || suggestion.Lot === undefined) return "-";

  return [suggestion.Lot, suggestion.LotRoudingUnit].filter(Boolean).join(" ");
};

const StockReturnNotificationModal = ({
  open,
  suggestions,
  onClose,
  onSuccess,
  onSubmit,
}: StockReturnNotificationModalProps) => {
  const globalContext = useGlobalContext() ?? {};
  const [recipients, setRecipients] = useState<string[]>([]);
  const [title, setTitle] = useState("");
  const [poReferences, setPoReferences] = useState<Record<string, string>>({});
  const [validationMessage, setValidationMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;

    setRecipients([]);
    setTitle(intl.get("suggestions.stockReturnNotification.defaultTitle"));
    setPoReferences({});
    setValidationMessage("");
    setIsSubmitting(false);
  }, [open]);

  const updatePoReference = (suggestionId: SuggestionId, value: string) => {
    setPoReferences((current) => ({
      ...current,
      [String(suggestionId)]: value,
    }));
  };

  const validateForm = (): boolean => {
    if (!suggestions.length || !suggestions.every(isStockReturnSuggestion)) {
      setValidationMessage(intl.get("suggestions.stockReturnNotification.invalidSelection"));
      return false;
    }

    if (!recipients.length || !recipients.every((email) => EMAIL_PATTERN.test(email.trim()))) {
      setValidationMessage(intl.get("suggestions.stockReturnNotification.recipientsRequired"));
      return false;
    }

    if (!title.trim()) {
      setValidationMessage(intl.get("suggestions.stockReturnNotification.titleRequired"));
      return false;
    }

    const hasMissingPoReference = suggestions.some(
      (suggestion) => !poReferences[String(suggestion.Id)]?.trim()
    );

    if (hasMissingPoReference) {
      setValidationMessage(intl.get("suggestions.stockReturnNotification.poReferenceRequired"));
      return false;
    }

    setValidationMessage("");
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const notificationPayload: StockReturnAcceptancePayload = {
        items: suggestions.map((suggestion) => ({
          suggestionId: suggestion.Id,
          poReference: poReferences[String(suggestion.Id)].trim(),
        })),
        notification: {
          recipients: recipients.map((email) => email.trim()),
          title: title.trim(),
        },
      };
      const response = onSubmit
        ? await onSubmit(notificationPayload)
        : await acceptSuggestionsWithStockReturnNotification({
            suggestions: suggestions.map((suggestion) => ({
              suggestionId: suggestion.Id,
              poReference: poReferences[String(suggestion.Id)].trim(),
              labeledFamilyCenterId: suggestion.LabeledFamilYCenterId,
            })),
            notification: notificationPayload.notification,
          });

      const failureMessage = getAcceptanceFailureMessage(response);
      globalContext.showFeedback?.(
        failureMessage ?? intl.get("suggestions.stockReturnNotification.success")
      );
      await onSuccess?.();
      onClose();
    } catch (error) {
      const message = formatErrorMessage(error);
      setValidationMessage(message);
      globalContext.showFeedback?.(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <GenericModal
      open={open}
      onRequestClose={onClose}
      modalStyle={{
        width: "min(1500px, calc(100vw - 32px))",
        maxHeight: "90vh",
        overflow: "hidden",
      }}
    >
      <S.Content>
        <S.Header>
          <S.Title>{intl.get("suggestions.stockReturnNotification.title")}</S.Title>
          <S.CloseButton
            type="button"
            aria-label={intl.get("suggestions.stockReturnNotification.close")}
            onClick={onClose}
          >
            <CloseIcon />
          </S.CloseButton>
        </S.Header>

        <S.Field>
          <S.Label>{intl.get("suggestions.stockReturnNotification.recipients")}</S.Label>
          <InputMultiEmail
            labelText=""
            emails={recipients}
            onChange={setRecipients}
            validateEmail={(email: string) => EMAIL_PATTERN.test(email)}
          />
        </S.Field>

        <S.Field>
          <S.Label htmlFor="stock-return-notification-title">
            {intl.get("suggestions.stockReturnNotification.subject")}
          </S.Label>
          <S.TextInput
            id="stock-return-notification-title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </S.Field>

        <S.SectionTitle>
          {intl.get("suggestions.stockReturnNotification.preview")}
        </S.SectionTitle>

        <S.PreviewWrapper>
          <S.PreviewTable>
            <thead>
              <tr>
                <th>{intl.get("commons.source")}</th>
                <th>{intl.get("commons.destiny")}</th>
                <S.MaterialHeader>{intl.get("commons.material")}</S.MaterialHeader>
                <th>{intl.get("commons.quantity")}</th>
                <th>{intl.get("commons.lot")}</th>
                <th>{intl.get("commons.colDate")}</th>
                <th>{intl.get("commons.deliveryDate")}</th>
                <th>{intl.get("commons.deliveryTime")}</th>
                <th>{intl.get("suggestions.stockReturnNotification.poReference")}</th>
              </tr>
            </thead>
            <tbody>
              {suggestions.map((suggestion) => (
                <tr key={suggestion.Id}>
                  <td>{getSourceText(suggestion)}</td>
                  <td>{getDestinationText(suggestion)}</td>
                  <S.MaterialCell title={getMaterialText(suggestion)}>
                    {getMaterialText(suggestion)}
                  </S.MaterialCell>
                  <td>{getQuantityText(suggestion)}</td>
                  <td>{getLotText(suggestion)}</td>
                  <td>{formatDate(suggestion.SuggestionCollectDate, "L")}</td>
                  <td>{formatDate(suggestion.SuggestionDeliveryDate, "L")}</td>
                  <td>{formatDate(suggestion.SuggestionDeliveryTime, "LT")}</td>
                  <td>
                    <S.PoReferenceInput
                      aria-label={`${intl.get("suggestions.stockReturnNotification.poReference")} ${suggestion.Id}`}
                      value={poReferences[String(suggestion.Id)] ?? ""}
                      onChange={(event) => updatePoReference(suggestion.Id, event.target.value)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </S.PreviewTable>
        </S.PreviewWrapper>

        {validationMessage && <S.ValidationMessage>{validationMessage}</S.ValidationMessage>}

        <S.Footer>
          <Button
            type="button"
            value={intl.get("geral.buttonsDefault.cancel")}
            onClick={onClose}
            disabled={isSubmitting}
            data-testid="stock-return-notification-cancel"
          />
          <Button
            type="button"
            buttonType="primary"
            value={intl.get("suggestions.stockReturnNotification.send")}
            onClick={handleSubmit}
            disabled={isSubmitting}
            data-testid="stock-return-notification-send"
          />
        </S.Footer>
      </S.Content>
    </GenericModal>
  );
};

export default StockReturnNotificationModal;
